import _ from 'lodash';
import React, { useContext, useState, createContext, useMemo, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router';
import { API } from '../api';
import { ConnectorDeal, ConnectorDealStatus, ConnectorTab } from '../types/types';
import { useAlert } from './AlertContext';
import { useUser } from './UserContext';


interface ConnectorDealsContextProviderProps {
  newDeals: ConnectorDeal[],
  inProgressDeals: ConnectorDeal[],
  doneDeals: ConnectorDeal[],
  newDealsTotalCount: number,
  inProgressDealsTotalCount: number,
  doneDealsTotalCount: number,
  currentPage: number,
  pageSize: number,
  fetchDeals: (status: ConnectorDealStatus, page: number) => Promise<void>,
  updateConnectorDealStatus: (id: number, status: ConnectorDealStatus) => Promise<void>,
  goToInvestorProfile: (profileId: number) => void,
  updateCurrentPage: (page: number, status: ConnectorDealStatus) => void,
  getIsLoading: (status: ConnectorDealStatus, page: number) => boolean,
  handleStatusChange: (id: number, status: ConnectorDealStatus) => void
}

enum LoadingState {
  Initial,
  Loading,
  Idle
}
interface ConnectorDealPageState {
  page: number,
  deals: ConnectorDeal[],
  loadingState: LoadingState
}


const pageSize = 10;

export const ConnectorDealsContext = createContext<ConnectorDealsContextProviderProps>({
  newDeals: [],
  inProgressDeals: [],
  doneDeals: [],
  newDealsTotalCount: 0,
  inProgressDealsTotalCount: 0,
  doneDealsTotalCount: 0,
  currentPage: 0,
  pageSize,
  fetchDeals: async (status: ConnectorDealStatus, page: number) => {},
  updateConnectorDealStatus: async () => {},
  goToInvestorProfile: (profileId: number) => {},
  updateCurrentPage: (page: number, status: ConnectorDealStatus) => {},
  getIsLoading: (status: ConnectorDealStatus, page: number) => false,
  handleStatusChange: (id: number, status: ConnectorDealStatus) => {}
});

export const useConnectorDeals = () => useContext(ConnectorDealsContext);


export function ConnectorDealsContextProvider(props: any) {
  const { postNewAlert, message } = useAlert();
  const { getAccessToken } = useUser();
  const history = useHistory();
  const { state: pageState, pathname } = history.location;

  const [newDeals, setNewDeals] = useState<ConnectorDealPageState>({ page: 0, deals: [], loadingState: LoadingState.Initial });
  const [inProgressDeals, setInProgressDeals] = useState<ConnectorDealPageState>({ page: 0, deals: [], loadingState: LoadingState.Initial });
  const [doneDeals, setDoneDeals] = useState<ConnectorDealPageState>({ page: 0, deals: [], loadingState: LoadingState.Initial });

  const [newDealsTotalCount, setNewDealsTotalCount] = useState<number>(0);
  const [inProgressDealsTotalCount, setInProgressDealsTotalCount] = useState<number>(0);
  const [doneDealsTotalCount, setDoneDealsTotalCount] = useState<number>(0);

  const getIsLoading = useCallback((status: ConnectorDealStatus, page: number) => {
    switch (status) {
      case ConnectorDealStatus.New:
        return newDeals.page === page && newDeals.loadingState === LoadingState.Loading;
      case ConnectorDealStatus.InProgress:
        return inProgressDeals.page === page && inProgressDeals.loadingState === LoadingState.Loading;
      default:
        return doneDeals.page === page && doneDeals.loadingState === LoadingState.Loading;
    }
  }, [newDeals, inProgressDeals, doneDeals]);

  const updateCurrentPage = (page: number) => {
    let newState = { page };
    if (typeof pageState === 'object') {
      newState = { ...pageState, page };
    }
    history.push(history.location.pathname, newState);
  };

  const currentPage = useMemo(() => {
    if (pageState && (pageState as any).page !== undefined) {
      const { page } = (pageState as any);
      return page || 1;
    }
    return 1;
  }, [pageState]);

  const currentStatus = useMemo(() => {
    const pathComponents = pathname.split('/');
    if (pathComponents.length) {
      const pathEnd = pathComponents[pathComponents.length - 1];
      switch (pathEnd) {
        case ConnectorTab.Done:
          return ConnectorDealStatus.Done;
        case ConnectorTab.InProgress:
          return ConnectorDealStatus.InProgress;
        default:
          return ConnectorDealStatus.New;
      }
    }
    return ConnectorDealStatus.New;
  }, [pathname]);

  const { children } = props;

  const countByStatus = {
    [ConnectorDealStatus.New]: newDealsTotalCount,
    [ConnectorDealStatus.InProgress]: inProgressDealsTotalCount,
    [ConnectorDealStatus.Done]: doneDealsTotalCount
  };

  const updateDeals = useCallback((status: ConnectorDealStatus, page: number, updatedDeals: ConnectorDeal[], loadingState: LoadingState) => {
    switch (status) {
      case ConnectorDealStatus.New:
        if (loadingState === LoadingState.Loading && page === newDeals.page && newDeals.loadingState !== LoadingState.Loading) {
          setNewDeals({ ...newDeals, loadingState });
        } else {
          setNewDeals({ page, deals: updatedDeals, loadingState });
        }
        break;
      case ConnectorDealStatus.InProgress:
        if (loadingState === LoadingState.Loading && page === inProgressDeals.page && inProgressDeals.loadingState !== LoadingState.Loading) {
          setInProgressDeals({ ...inProgressDeals, loadingState });
        } else {
          setInProgressDeals({ page, deals: updatedDeals, loadingState });
        }
        break;
      case ConnectorDealStatus.Done:
        if (loadingState === LoadingState.Loading && page === doneDeals.page && doneDeals.loadingState !== LoadingState.Loading) {
          setDoneDeals({ ...doneDeals, loadingState });
        } else {
          setDoneDeals({ page, deals: updatedDeals, loadingState });
        }
        break;
      default:
        break;
    }
  }, [newDeals, inProgressDeals, doneDeals]);

  const updateDealsCount = (status: ConnectorDealStatus, updatedCount: number) => {
    switch (status) {
      case ConnectorDealStatus.New:
        setNewDealsTotalCount(updatedCount);
        break;
      case ConnectorDealStatus.InProgress:
        setInProgressDealsTotalCount(updatedCount);
        break;
      case ConnectorDealStatus.Done:
        setDoneDealsTotalCount(updatedCount);
        break;
      default:
        break;
    }
  };

  const findDealById = (id: number) => {
    const allDeals = [...newDeals.deals, ...inProgressDeals.deals, ...doneDeals.deals];
    return allDeals.find((deal) => deal.id === id);
  };

  const shouldFetch = useCallback((status: ConnectorDealStatus, page: number = 1) => {
    switch (status) {
      case ConnectorDealStatus.New:
        return newDeals.page !== page || newDeals.loadingState !== LoadingState.Loading;
      case ConnectorDealStatus.InProgress:
        return inProgressDeals.page !== page || inProgressDeals.loadingState !== LoadingState.Loading;
      default:
        return doneDeals.page !== page || doneDeals.loadingState !== LoadingState.Loading;
    }
  }, [newDeals, inProgressDeals, doneDeals]);

  const fetchDealsByStatus = useCallback(async (status: ConnectorDealStatus, page: number = 1) => {
    const errorMessage = `error fetching connector deals for status ${status} : `;
    if (!shouldFetch(status, page)) {
      return;
    }
    updateDeals(status, page, [], LoadingState.Loading);
    const token = await getAccessToken();
    try {
      const from = (page - 1) * pageSize;
      const { deals: apiDeals, total, matches } = await API.getConnectorDeals(token, status, from, pageSize);
      const dealsWithMatches = apiDeals.map((deal) => {
        const matchList = matches.filter((m) => (m.entId === deal.entrepreneurProfileId));
        const matched = (matchList && matchList.length > 0) ? matchList[0].scoredInvestors : [];
        const matchCount = (matchList && matchList.length > 0) ? matchList[0].totalInvestorCount : 0;
        deal.matches = matched;
        deal.totalMatchCount = matchCount;
        return deal;
      });
      if (status === currentStatus) {
        if (page === currentPage) {
          updateDeals(status, page, dealsWithMatches, LoadingState.Idle);
        }
      } else {
        updateDeals(status, page, dealsWithMatches, LoadingState.Idle);
      }
      updateDealsCount(status, total);
    } catch (e: unknown) {
      console.error(errorMessage, e);
      updateDeals(status, page, [], LoadingState.Idle);
      postNewAlert(`An error occurred fetching ${status === ConnectorDealStatus.InProgress ? 'in progress' : status} deals.`);
    }
  }, [shouldFetch, getAccessToken, updateDeals, currentStatus, currentPage, postNewAlert]);

  useEffect(() => {
    if (newDeals.loadingState === LoadingState.Initial) {
      fetchDealsByStatus(ConnectorDealStatus.New);
    }
    if (inProgressDeals.loadingState === LoadingState.Initial) {
      fetchDealsByStatus(ConnectorDealStatus.InProgress);
    }
    if (doneDeals.loadingState === LoadingState.Initial) {
      fetchDealsByStatus(ConnectorDealStatus.Done);
    }
  }, [fetchDealsByStatus, doneDeals, inProgressDeals, newDeals]);

  const updateConnectorDealStatus = async (id: number, status: ConnectorDealStatus) => {
    const errorMessage = 'error updating connector deal status: ';
    try {
      const token = await getAccessToken();
      await API.updateConnectorDeal(token, id, { status });
      handleStatusChange(id, status);
    } catch (e: unknown) {
      console.error(errorMessage, e);
      postNewAlert('An error occurred updating deal status.');
    }
  };

  const handleStatusChange = (id: number, status: ConnectorDealStatus) => {
    const deal = findDealById(id);
    if (!deal) { return; }
    updateDealsCount(status, countByStatus[status] + 1);
    const oldStatus = deal.status;
    const newPage = Math.max(1, (countByStatus[oldStatus] - 1) % pageSize === 0 ? currentPage - 1 : currentPage);
    fetchDealsByStatus(status, 1);
    fetchDealsByStatus(oldStatus, newPage);
    if (newPage !== currentPage) {
      updateCurrentPage(newPage);
    }
  };

  const goToInvestorProfile = (profileId: number) => {
    history.push(`/investor/${profileId}`);
  };

  useEffect(() => {
    if (currentStatus === ConnectorDealStatus.New && newDeals.page !== currentPage && newDeals.loadingState !== LoadingState.Initial) {
      fetchDealsByStatus(currentStatus, currentPage);
    }
    if (currentStatus === ConnectorDealStatus.InProgress && inProgressDeals.page !== currentPage && inProgressDeals.loadingState !== LoadingState.Initial) {
      fetchDealsByStatus(currentStatus, currentPage);
    }
    if (currentStatus === ConnectorDealStatus.Done && doneDeals.page !== currentPage && doneDeals.loadingState !== LoadingState.Initial) {
      fetchDealsByStatus(currentStatus, currentPage);
    }
  }, [currentStatus, currentPage, fetchDealsByStatus, newDeals.page, doneDeals.page, inProgressDeals.page, newDeals.loadingState, doneDeals.loadingState, inProgressDeals.loadingState]);

  // handle new deals being created as a result of inviting an existing ent
  useEffect(() => {
    if (message != null && message.search('Deals were created') >= 0) {
      fetchDealsByStatus(ConnectorDealStatus.New, newDeals.page);
    }
    // todo: fetchDealsByStatus dependency causes a bunch of redundant fetching here.
    // my eternal respect and admiration to whomever can fix that without breaking something else.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, newDeals.page]);

  return (
    <ConnectorDealsContext.Provider value={{
      newDeals: newDeals.deals,
      inProgressDeals: inProgressDeals.deals,
      doneDeals: doneDeals.deals,
      newDealsTotalCount,
      inProgressDealsTotalCount,
      doneDealsTotalCount,
      currentPage,
      pageSize,
      fetchDeals: fetchDealsByStatus,
      updateConnectorDealStatus,
      goToInvestorProfile,
      updateCurrentPage,
      getIsLoading,
      handleStatusChange
    }}
    >
      {children}
    </ConnectorDealsContext.Provider>
  );
}
